import { useMutation, useQueryClient } from "@tanstack/react-query";
import { EditIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Dialog } from "react-aria-components";
import type z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal, ModalHeading } from "@/components/base/modal";
import { Popover } from "@/components/base/popover";
import { Radio, RadioGroup } from "@/components/base/radio-group";
import {
  usePools,
  usePoolsQueryOptions,
  useTeam,
  useTeamsQueryOptions,
} from "@/components/tournaments/context";
import { useTRPC } from "@/integrations/trpc/react";
import { updatePoolSchema } from "@/lib/team-schemas";
import { isNotNullOrUndefined } from "@/utils/types";

import { TeamNames } from "../names";

export type EditPoolFormProps = {
  tournamentDivisionTeamId: number;
  poolId: number;
};

export function EditPoolForm({
  tournamentDivisionTeamId,
  poolId,
}: EditPoolFormProps) {
  const [isOpen, setOpen] = useState(false);
  const triggerRef = useRef(null);

  const [seedStrategy, setSeedStrategy] = useState("calculate");

  const teamsQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.updatePool.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamsQueryOptions);
      queryClient.invalidateQueries(poolsQueryOptions);

      setOpen(false);
    },
  });

  const pools = usePools();

  const schema = updatePoolSchema.omit({ id: true });

  const form = useAppForm({
    defaultValues: {
      poolId,
      seed: null as number | null | undefined,
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { poolId, seed } }) => {
      mutate({
        id: tournamentDivisionTeamId,
        poolId,
        seed,
      });
    },
  });

  useEffect(() => {
    if (seedStrategy === "calculate") {
      form.setFieldValue("seed", null);
    } else {
      const selectedPool = pools.find(
        ({ id }) => id === form.state.values.poolId,
      );

      form.setFieldValue(
        "seed",
        Math.max(
          0,
          ...(selectedPool?.teams
            .map(({ seed }) => seed)
            .filter(isNotNullOrUndefined) ?? []),
        ) + 1,
      );
    }
  }, [form, seedStrategy, pools]);

  return (
    <>
      <Button
        variant="icon"
        size="sm"
        ref={triggerRef}
        onPress={() => setOpen(true)}
      >
        <EditIcon />
      </Button>
      <Popover triggerRef={triggerRef} isOpen={isOpen} onOpenChange={setOpen}>
        <Dialog aria-label="Set team and waitlist capacity">
          <form
            className="flex min-w-xs flex-col space-y-4 p-3"
            onSubmit={(e) => {
              e.preventDefault();

              form.handleSubmit();
            }}
          >
            {failureReason && (
              <form.AppForm>
                <form.Alert
                  title={"Unable to update pool"}
                  description={failureReason.message}
                />
              </form.AppForm>
            )}

            <form.AppField name="poolId">
              {(field) => (
                <field.Select
                  field={field}
                  name="poolId"
                  label="Desired Pool"
                  options={pools.map(({ id, name }) => ({
                    value: id,
                    display: name.toUpperCase(),
                  }))}
                />
              )}
            </form.AppField>

            <RadioGroup
              orientation="vertical"
              label="Seed Strategy"
              value={seedStrategy}
              onChange={setSeedStrategy}
            >
              <Radio value="calculate">Recalculate</Radio>
              <Radio value="manual">Manual</Radio>
            </RadioGroup>

            {seedStrategy === "manual" && (
              <form.AppForm>
                <form.Subscribe
                  selector={(state) =>
                    pools.find((p) => p.id === state.values.poolId)
                  }
                >
                  {(pool) => (
                    <form.AppField name="seed">
                      {(field) => (
                        <field.Number
                          field={field}
                          name="seed"
                          label="Desired Seed"
                          minValue={1}
                          maxValue={pool ? pool.teams.length + 1 : undefined}
                        />
                      )}
                    </form.AppField>
                  )}
                </form.Subscribe>
              </form.AppForm>
            )}

            <form.AppForm>
              <form.Footer>
                <form.SubmitButton requireChange={false}>
                  Save
                </form.SubmitButton>
              </form.Footer>
            </form.AppForm>
          </form>
        </Dialog>
      </Popover>
    </>
  );
}

export type EditPoolFormModalProps = {
  tournamentDivisionTeamId: number;
  poolId?: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditPoolFormModal({
  tournamentDivisionTeamId,
  poolId: poolIdProp,
  ...props
}: EditPoolFormModalProps) {
  const team = useTeam(tournamentDivisionTeamId);

  const [seedStrategy, setSeedStrategy] = useState("calculate");

  const teamsQueryOptions = useTeamsQueryOptions();
  const poolsQueryOptions = usePoolsQueryOptions();

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.updatePool.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(teamsQueryOptions);
      queryClient.invalidateQueries(poolsQueryOptions);

      props.onOpenChange(false);
    },
  });

  const pools = usePools();
  const poolId = poolIdProp ?? pools[0]?.id;

  const schema = updatePoolSchema.omit({ id: true });

  const form = useAppForm({
    defaultValues: {
      poolId,
      seed: null as number | null | undefined,
    } as z.infer<typeof schema>,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: ({ value: { poolId, seed } }) => {
      mutate({
        id: tournamentDivisionTeamId,
        poolId,
        seed,
      });
    },
  });

  useEffect(() => {
    if (seedStrategy === "calculate") {
      form.setFieldValue("seed", null);
    } else {
      const selectedPool = pools.find(
        ({ id }) => id === form.state.values.poolId,
      );

      form.setFieldValue(
        "seed",
        Math.max(
          0,
          ...(selectedPool?.teams
            .map(({ seed }) => seed)
            .filter(isNotNullOrUndefined) ?? []),
        ) + 1,
      );
    }
  }, [form, seedStrategy, pools]);

  return (
    <Modal {...props}>
      <div className="relative flex flex-col space-y-8 p-3">
        <ModalHeading>Edit Pool</ModalHeading>

        <p>
          Edit pool for{" "}
          <TeamNames
            className="font-semibold italic"
            players={team?.team.players || []}
            link={false}
          />
          .
        </p>

        <form
          className="flex min-w-xs flex-col space-y-4 p-3"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to update pool"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField name="poolId">
            {(field) => (
              <field.Select
                field={field}
                name="poolId"
                label="Desired Pool"
                options={pools.map(({ id, name }) => ({
                  value: id,
                  display: name.toUpperCase(),
                }))}
              />
            )}
          </form.AppField>

          <RadioGroup
            orientation="vertical"
            label="Pool Seed Strategy"
            value={seedStrategy}
            onChange={setSeedStrategy}
          >
            <Radio value="calculate">Recalculate</Radio>
            <Radio value="manual">Manual</Radio>
          </RadioGroup>

          {seedStrategy === "manual" && (
            <form.AppForm>
              <form.Subscribe
                selector={(state) =>
                  pools.find((p) => p.id === state.values.poolId)
                }
              >
                {(pool) => (
                  <form.AppField name="seed">
                    {(field) => (
                      <field.Number
                        field={field}
                        name="seed"
                        label="Desired Seed"
                        minValue={1}
                        maxValue={pool ? pool.teams.length + 1 : undefined}
                      />
                    )}
                  </form.AppField>
                )}
              </form.Subscribe>
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => props.onOpenChange(false)}>Cancel</Button>
              <form.SubmitButton requireChange={false}>Save</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
